import { Injectable } from "@angular/core";

export type NavTabPath = [navTabNumber: number];
export type PagePath = [navTabNumber: number, pageNumber: number];
export type SectionPath = [navTabNumber: number, pageNumber: number, sectionCode: string];
export type QuestionPath = [navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string];

@Injectable({
  providedIn: 'root'
})
export class ResponseParserService {

  findNavTabModel(model: any, navTabNumber: number): any {
    return model?.form?.tabs?.[`tab${navTabNumber}`];
  }

  findPageModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number): any {
    const isTryingToFindRepeatingPageItem = pageIndex !== null && pageIndex !== undefined;
    return isTryingToFindRepeatingPageItem
      ? this.findNavTabModel(model, navTabNumber)?.[`page${pageNumber}`]?.[pageIndex]
      : this.findNavTabModel(model, navTabNumber)?.[`page${pageNumber}`];
  }

  findSectionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number): any {
    const isTryingToFindRepeatingSectionItem = sectionIndex !== null && sectionIndex !== undefined;
    return isTryingToFindRepeatingSectionItem
      ? this.findPageModel(model, navTabNumber, pageNumber, pageIndex)?.[sectionCode]?.[sectionIndex]
      : this.findPageModel(model, navTabNumber, pageNumber, pageIndex)?.[sectionCode];
  }

  findQuestionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, questionCode: string) {
    return this.findSectionModel(model, navTabNumber, pageNumber, pageIndex, sectionCode, sectionIndex)?.[questionCode];
  }

  replaceNavTabModel(model: any, navTabNumber: number, newNavTabModel: any): boolean {
    const navTabParentModel = model?.form?.tabs;
    if (navTabParentModel) {
      navTabParentModel[`tab${navTabNumber}`] = newNavTabModel;
      return true;
    }
    return false;
  }

  replacePageModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, newPageModel: any): boolean {
    const pageParentModel = this.findNavTabModel(model, navTabNumber);
    if (pageParentModel) {
      const isTryingToReplaceRepeatingPageItem = pageIndex !== null && pageIndex !== undefined;
      if (isTryingToReplaceRepeatingPageItem) {
        const pageItemParentModel = pageParentModel[`page${pageNumber}`];
        if (pageItemParentModel) {
          pageItemParentModel[pageIndex] = newPageModel;
          return true;
        }
      }
      else {
        pageParentModel[`page${pageNumber}`] = newPageModel;
        return true;
      }
    }
    return false;
  }

  replaceSectionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, newSectionModel: any): boolean {
    const sectionParentModel = this.findPageModel(model, navTabNumber, pageNumber, pageIndex);
    if (sectionParentModel) {
      const isTryingToReplaceRepeatingSectionItem = sectionIndex !== null && sectionIndex !== undefined;
      if (isTryingToReplaceRepeatingSectionItem) {
        const sectionItemParentModel = sectionParentModel[sectionCode];
        if (sectionItemParentModel) {
          sectionItemParentModel[sectionIndex] = newSectionModel;
          return true;
        }
      }
      else {
        sectionParentModel[sectionCode] = newSectionModel;
        return true;
      }
    }
    return false;
  }

  replaceQuestionModel(model: any, navTabNumber: number, pageNumber: number, pageIndex: number, sectionCode: string, sectionIndex: number, questionCode: string, newQuestionModel: any): boolean {
    const questionParentModel = this.findSectionModel(model, navTabNumber, pageNumber, pageIndex, sectionCode, sectionIndex);
    if (questionParentModel) {
      questionParentModel[questionCode] = newQuestionModel;
      return true;
    }
    return false;
  }

  // Making sure that a SingleCheckBox question, after it is ticked and then un-ticked again, has a model of undefined instead of false, so that validations work as expected.
  // For example, if the question is mandatory, changing the model from false to undefined ensures that the client-side validation will be triggered.
  handleSingleCheckBoxQuestionChanges(
    getModel: () => any,
    setModel: (model: any) => void,
    singleCheckBoxQuestionPaths: QuestionPath[]): number {

    const model = getModel();

    let resetCount = 0;
    for (const [navTabNumber, pageNumber, sectionCode, questionCode] of singleCheckBoxQuestionPaths ?? []) {

      const getIndexes = (objectOrArrayOfObjects: any) => Array.isArray(objectOrArrayOfObjects) ? [...objectOrArrayOfObjects.keys()] : [null];

      const pageIndexes = getIndexes(this.findPageModel(model, navTabNumber, pageNumber, null));
      for (const pageIndex of pageIndexes) {

        const sectionIndexes = getIndexes(this.findSectionModel(model, navTabNumber, pageNumber, pageIndex, sectionCode, null))
        for (const sectionIndex of sectionIndexes) {

          const singleCheckBoxQuestionModel = this.findQuestionModel(model, navTabNumber, pageNumber, pageIndex, sectionCode, sectionIndex, questionCode);
          if (singleCheckBoxQuestionModel === false) {
            this.replaceQuestionModel(model, navTabNumber, pageNumber, pageIndex, sectionCode, sectionIndex, questionCode, undefined);
            resetCount++;
          }
        }
      }
    }

    if (resetCount > 0) {
      setModel({ ...model });
    }

    return resetCount;
  }
}
